import { useState } from "react";
import type { ConfigResponse } from "@site-alerter/shared";
import { ApiError, postPause } from "../api.js";
import { absoluteTime } from "../time.js";

interface Props {
  idToken: string;
  config: ConfigResponse;
  onChange: (config: ConfigResponse) => void;
}

const PRESETS: { label: string; hours: number | null }[] = [
  { label: "1 hour", hours: 1 },
  { label: "8 hours", hours: 8 },
  { label: "24 hours", hours: 24 },
  { label: "Indefinitely", hours: null },
];

export function Cutoff({ idToken, config, onChange }: Props): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [choosing, setChoosing] = useState(false);

  async function apply(enabled: boolean, hours: number | null): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const pausedUntil =
        enabled || hours == null ? null : new Date(Date.now() + hours * 3_600_000).toISOString();
      onChange(await postPause(idToken, enabled, pausedUntil));
      setChoosing(false);
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? (cause.payload.details?.join(", ") ?? cause.message)
          : String(cause),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>Cutoff</h2>

      <div className={`state ${config.effectivelyEnabled ? "state-on" : "state-off"}`}>
        {config.effectivelyEnabled ? "Sending is ON" : "Sending is PAUSED"}
      </div>

      {config.effectivelyEnabled ? (
        choosing ? (
          <div className="presets">
            {/*
              Pausing drops alerts rather than deferring them, so the cost is
              stated before the choice, not explained afterwards.
            */}
            <p className="warn-inline">
              <strong>Breaches found while paused are never sent.</strong> They are recorded as
              seen so they cannot arrive later. Only pause if you are willing to miss them.
            </p>
            <p className="muted">Pause for how long?</p>
            {PRESETS.map((preset) => (
              <button
                key={preset.label}
                type="button"
                disabled={busy}
                onClick={() => void apply(false, preset.hours)}
              >
                {preset.label}
              </button>
            ))}
            <button type="button" disabled={busy} onClick={() => setChoosing(false)}>
              Cancel
            </button>
          </div>
        ) : (
          <button type="button" className="danger" disabled={busy} onClick={() => setChoosing(true)}>
            Pause sending
          </button>
        )
      ) : (
        <div className="resume">
          {config.haltedAt != null && (
            <p className="warn-inline">
              <strong>This was not a manual pause.</strong> The worker stopped sending because it
              found a full alert&rsquo;s worth of new breaches on two consecutive runs — usually a
              sign the source page changed. Check it before resuming.
            </p>
          )}
          {config.pausedUntil != null && (
            <p className="muted">Scheduled to resume at {absoluteTime(config.pausedUntil)}.</p>
          )}
          <p className="muted">
            Breaches found during this pause have been recorded as seen and will not be sent when
            sending resumes. Anything found afterwards alerts normally.
          </p>
          <div className="actions">
            <button
              type="button"
              className="primary"
              disabled={busy}
              onClick={() => void apply(true, null)}
            >
              {busy ? "Resuming…" : "Resume sending"}
            </button>
          </div>
        </div>
      )}

      {error != null && <p className="error">{error}</p>}

      <p className="muted footnote">
        The worker keeps running while paused — it still checks the sites and records what it
        finds, it just does not send.
      </p>
    </section>
  );
}
